import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { listNews } from '../../shared/api/news.api'
import { useAuthStore } from '../auth/authStore'
import { PageLoader } from '../../shared/ui/Spinner'
import { NewsComposer } from './NewsComposer'
import { NewsPostCard } from './NewsPostCard'
import { canWriteNews } from './utils'

export function NewsPage() {
  const role = useAuthStore((s) => s.user?.role)
  const canWrite = canWriteNews(role)
  const [searchParams, setSearchParams] = useSearchParams()
  const highlightId = searchParams.get('postId')

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['news', { page: 1, limit: 50 }],
    queryFn: () => listNews({ page: 1, limit: 50 }),
    staleTime: 30_000,
  })

  // Подсветка + прокрутка к посту из уведомления (?postId=...).
  // Чистим параметр после первой обработки, чтобы при возврате на страницу
  // мы не перепрыгивали обратно.
  const scrolledRef = useRef<string | null>(null)
  useEffect(() => {
    if (!highlightId || !data) return
    if (scrolledRef.current === highlightId) return
    const el = document.getElementById(`news-${highlightId}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      scrolledRef.current = highlightId
      const t = setTimeout(() => {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            next.delete('postId')
            return next
          },
          { replace: true },
        )
      }, 1500)
      return () => clearTimeout(t)
    }
  }, [highlightId, data, setSearchParams])

  if (isLoading) return <PageLoader />
  if (isError || !data) {
    return (
      <div className="bg-white rounded-2xl border border-red-200 p-6 text-center">
        <p className="text-sm text-red-700 mb-2">Не удалось загрузить ленту новостей</p>
        <button
          onClick={() => refetch()}
          className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
        >
          Повторить
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Лента новостей</h1>
        <span className="text-xs text-gray-400">Всего: {data.total}</span>
      </header>

      {canWrite && <NewsComposer />}

      {data.items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-400">
          Пока новостей нет.
        </div>
      ) : (
        <div className="space-y-3">
          {data.items.map((post) => (
            <NewsPostCard key={post.id} post={post} highlighted={post.id === highlightId} />
          ))}
        </div>
      )}
    </div>
  )
}
